// Cascade-Move/Resize fuer Container-Objekte (Block + Region).
// Block-Move zieht Regions (per blockId-FK) + Objekte mit parentRegionId in regions(block) mit.
// Region-Move zieht eigene parentRegionId-Children.
// Block-Resize skaliert Regions proportional (relative Offsets bleiben proportional); Children
// behalten ihre Groesse, ihre Position wird per Region-Resize indirekt mitgenommen.
//
// Locked-Objekte werden geskippt (Schema-Feld bereits in BaseObjectSchema).

import { isCanvasObject } from "@ms-whiteboard/shared";
import type { CanvasObject } from "@ms-whiteboard/shared";
import type * as Y from "yjs";

/**
 * Sammelt alle Objekt-IDs die einer Drag-/Move-Operation folgen sollen.
 *
 *  - Wenn `id` ein Block ist: alle Regions mit `blockId === id` + alle Objekte mit
 *    `parentRegionId` in dieser Region-Liste.
 *  - Wenn `id` eine Region ist: alle Objekte mit `parentRegionId === id`.
 *  - Wenn `id` ein Frame ist (V2 Slice 5): alle Objekte mit `parentFrameId === id`.
 *  - Sonst: nichts zusaetzlich.
 *
 * Das Result enthaelt die `seedIds` selbst (damit Caller einfach iterieren kann)
 * und alle abhaengigen IDs. Locked-Objekte werden _nicht_ gefiltert — das uebernimmt der Caller.
 */
export function collectCascadeIds(yMap: Y.Map<unknown>, seedIds: Iterable<string>): Set<string> {
  const result = new Set<string>();
  const seeds = Array.from(seedIds);
  for (const id of seeds) result.add(id);

  // Pass 1: Block-Seeds → Regions sammeln
  const regionIdsByBlock = new Map<string, string[]>();
  for (const id of seeds) {
    const raw = yMap.get(id);
    if (!isCanvasObject(raw)) continue;
    if (raw.type !== "block") continue;
    const regions: string[] = [];
    for (const [otherId, value] of yMap.entries()) {
      if (!isCanvasObject(value)) continue;
      if (value.type !== "region") continue;
      if (value.blockId === id) {
        regions.push(otherId);
        result.add(otherId);
      }
    }
    regionIdsByBlock.set(id, regions);
  }

  // Pass 2: alle Region-IDs sammeln (Seeds + indirekt via Block) → parentRegionId-Children
  const allRegionIds = new Set<string>();
  for (const id of seeds) {
    const raw = yMap.get(id);
    if (isCanvasObject(raw) && raw.type === "region") allRegionIds.add(id);
  }
  for (const regions of regionIdsByBlock.values()) {
    for (const r of regions) allRegionIds.add(r);
  }

  // Pass 3: Frame-Seeds (V2 Slice 5) → parentFrameId-Children
  const frameSeeds = new Set<string>();
  for (const id of seeds) {
    const raw = yMap.get(id);
    if (isCanvasObject(raw) && raw.type === "frame") frameSeeds.add(id);
  }

  if (allRegionIds.size === 0 && frameSeeds.size === 0) return result;

  for (const [otherId, value] of yMap.entries()) {
    if (!isCanvasObject(value)) continue;
    if (value.parentRegionId && allRegionIds.has(value.parentRegionId)) {
      result.add(otherId);
      continue;
    }
    if (value.parentFrameId && frameSeeds.has(value.parentFrameId)) {
      result.add(otherId);
    }
  }

  return result;
}

/**
 * Filtert eine Set-of-IDs nach `!locked`. Wird beim Drag/Resize benutzt damit gelockte
 * Container Children nicht mitziehen wenn sie selbst nicht bewegt werden duerften.
 */
export function filterUnlocked(yMap: Y.Map<unknown>, ids: Iterable<string>): Set<string> {
  const result = new Set<string>();
  for (const id of ids) {
    const raw = yMap.get(id);
    if (!isCanvasObject(raw)) continue;
    if (raw.locked) continue;
    result.add(id);
  }
  return result;
}

/**
 * Berechnet die Regions die zum Block gehoeren — fuer Block-Resize-Cascade.
 * Liefert die Regions als CanvasObject-Snapshots (fuer Skalierung der relativen Offsets).
 */
export function findRegionsOfBlock(yMap: Y.Map<unknown>, blockId: string): CanvasObject[] {
  const result: CanvasObject[] = [];
  for (const value of yMap.values()) {
    if (!isCanvasObject(value)) continue;
    if (value.type === "region" && value.blockId === blockId) result.push(value);
  }
  return result;
}

/**
 * Skaliert die Regions eines Blocks proportional zur Block-Geometrie-Aenderung.
 *
 * Relative Offsets (Region.x - Block.oldX, Region.y - Block.oldY) und (Region.w, Region.h)
 * werden mit den Scale-Factors multipliziert. Anchor-Behaviour kommt vom Block-Resize
 * selbst (Block.x/y wurde von der Resize-Logik schon korrekt gesetzt).
 *
 * Children (Objekte mit parentRegionId) werden hier NICHT skaliert — sie behalten ihre
 * Groesse, ihre Position wird vom Region-Move (via collectCascadeIds) mitgenommen sobald
 * der Caller die Region in derselben Transaction verschoben hat.
 */
export function cascadeResizeRegions(
  yMap: Y.Map<unknown>,
  blockId: string,
  oldBlock: { x: number; y: number; width: number; height: number },
  newBlock: { x: number; y: number; width: number; height: number },
): void {
  if (oldBlock.width <= 0 || oldBlock.height <= 0) return;
  const sx = newBlock.width / oldBlock.width;
  const sy = newBlock.height / oldBlock.height;
  const regions = findRegionsOfBlock(yMap, blockId);
  for (const region of regions) {
    if (region.locked) continue;
    if (region.type !== "region") continue;
    const relX = region.x - oldBlock.x;
    const relY = region.y - oldBlock.y;
    const nextX = newBlock.x + relX * sx;
    const nextY = newBlock.y + relY * sy;
    const nextW = region.width * sx;
    const nextH = region.height * sy;
    // Children (parentRegionId === region.id) bewegen sich relativ zu Region-Move-Delta:
    const dxChildren = nextX - region.x;
    const dyChildren = nextY - region.y;
    yMap.set(region.id, {
      ...region,
      x: nextX,
      y: nextY,
      width: nextW,
      height: nextH,
    });
    if (dxChildren !== 0 || dyChildren !== 0) {
      moveChildrenOfRegion(yMap, region.id, dxChildren, dyChildren);
    }
  }
}

function moveChildrenOfRegion(
  yMap: Y.Map<unknown>,
  regionId: string,
  dx: number,
  dy: number,
): void {
  for (const [id, value] of yMap.entries()) {
    if (!isCanvasObject(value)) continue;
    if (value.parentRegionId !== regionId) continue;
    if (value.locked) continue;
    yMap.set(id, { ...value, x: value.x + dx, y: value.y + dy });
  }
}
